import React, { useEffect, useRef } from 'react';
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';

export type ModalType = 'info' | 'success' | 'warning' | 'error' | 'confirm';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title: string;
  message: string | React.ReactNode;
  type?: ModalType;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'info',
  confirmText = 'OK',
  cancelText = 'Cancel',
  showCancel = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLElement | null>(null);
  const lastFocusableRef = useRef<HTMLElement | null>(null);

  // Focus management for accessibility
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Find all focusable elements
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length > 0) {
        firstFocusableRef.current = focusableElements[0] as HTMLElement;
        lastFocusableRef.current = focusableElements[focusableElements.length - 1] as HTMLElement;
        firstFocusableRef.current.focus();
      }

      // Trap focus within modal
      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstFocusableRef.current) {
            e.preventDefault();
            lastFocusableRef.current?.focus();
          }
        } else {
          if (document.activeElement === lastFocusableRef.current) {
            e.preventDefault();
            firstFocusableRef.current?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTabKey);
      return () => document.removeEventListener('keydown', handleTabKey);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // const iconMap = {
  //   info: <Info className="w-6 h-6 text-blue-600" />,
  //   success: <CheckCircle className="w-6 h-6 text-green-600" />,
  //   warning: <AlertTriangle className="w-6 h-6 text-yellow-600" />,
  //   error: <AlertCircle className="w-6 h-6 text-red-600" />,
  //   confirm: <AlertCircle className="w-6 h-6 text-blue-600" />,
  // }; // Reserved for future use

  // const buttonColorMap = {
  //   info: 'bg-blue-600 hover:bg-blue-700',
  //   success: 'bg-green-600 hover:bg-green-700',
  //   warning: 'bg-yellow-600 hover:bg-yellow-700',
  //   error: 'bg-red-600 hover:bg-red-700',
  //   confirm: 'bg-blue-600 hover:bg-blue-700',
  // }; // Reserved for future use

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      aria-labelledby="modal-title"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop with blur */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal with scale animation */}
      <div
        ref={modalRef}
        className="relative bg-white rounded-2xl shadow-large max-w-md w-full animate-scale-in z-10 border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className={`px-6 py-4 rounded-t-2xl ${
          type === 'error' ? 'bg-gradient-to-r from-danger-500 to-danger-600' :
          type === 'success' ? 'bg-gradient-to-r from-success-500 to-success-600' :
          type === 'warning' ? 'bg-gradient-to-r from-warning-500 to-warning-600' :
          type === 'confirm' ? 'bg-gradient-to-r from-primary-500 to-primary-600' :
          'bg-gradient-to-r from-accent-500 to-accent-600'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Icon with animation */}
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                {type === 'error' && <AlertCircle className="w-6 h-6 text-white" />}
                {type === 'success' && <CheckCircle className="w-6 h-6 text-white" />}
                {type === 'warning' && <AlertTriangle className="w-6 h-6 text-white" />}
                {type === 'info' && <Info className="w-6 h-6 text-white" />}
                {type === 'confirm' && <AlertCircle className="w-6 h-6 text-white" />}
              </div>
              <h3 id="modal-title" className="text-lg font-bold text-white">{title}</h3>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-1 transition-all duration-200"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Body */}
        <div className="px-6 py-4">
          {typeof message === 'string' ? (
            <p className="text-gray-700">{message}</p>
          ) : (
            message
          )}
        </div>

        {/* Footer with enhanced buttons */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
          {showCancel && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium"
            >
              {cancelText}
            </button>
          )}
          {onConfirm && (
            <button
              onClick={handleConfirm}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 ${
                type === 'error' ? 'bg-danger-600 hover:bg-danger-700 text-white' :
                type === 'success' ? 'bg-success-600 hover:bg-success-700 text-white' :
                'bg-primary-600 hover:bg-primary-700 text-white'
              }`}
            >
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;

